﻿import { generateTextWithGemini } from "./LegalAPIs";
import { sanitizeEvidenceName } from "../utils/formatters";
import { rotuloOuAntigo, textoCitacao } from "../utils/numeracaoAnexos";

const BASE_VIEWER_URL = "https://rodrigodemeterco.adv.br/view";


const stripLeadingAnexoPrefix = (raw) => {
  const t = String(raw || "").trim();
  if (!t) return "";
  return t.replace(/^ANEXO[_\s]*\d{1,3}[_\s]*[-\u2013]?[_\s]*/i, "").trim();
};

const cleanDescForCitation = (raw) => {
  let t = stripLeadingAnexoPrefix(raw);
  if (!t) t = String(raw || "").trim();
  t = t.replace(/\.(pdf|docx?|xlsx?|pptx?|jpe?g|png|gif|bmp|tiff?|txt|rtf|csv|zip|rar|7z|msg|eml)$/i, "");
  t = t.replace(/[_]/g, " ");
  t = t.toUpperCase();
  t = t.replace(/^\s*(?:ANEXOS?)\s*[-_.:]*\s*/i, "");
  t = t.replace(/\s{2,}/g, " ").trim();
  return t || "PROVA DOCUMENTAL";
};

const getAttachmentUrl = (attachment) => {
  const id = String(attachment?.id || "").trim();
  if (id) {
    const desc = String(attachment?.description || "").trim();
    const titleParam = desc ? `?title=${encodeURIComponent(desc)}` : "";
    return `${BASE_VIEWER_URL}/${id}${titleParam}`;
  }
  const url = String(attachment?.url || "").trim();
  return url;
};

export const insertAttachmentCitation = async (attachment, index) => {
  try {
    await Word.run(async (context) => {
      const range = context.document.getSelection();
      const cc = range.insertContentControl();

      cc.tag = `ANEXO_ID_${attachment.id}`;
      cc.title = "Citação de Anexo";
      cc.appearance = "Hidden";

      const citationText = cleanDescForCitation(attachment?.description);
      // O Word é ESPELHO (decisão do titular, 24/09/2026): imprime o rótulo pronto que o Portal mandou
      // ("ANEXO 30" / "ANEXO 007"). Sem rótulo (Portal antigo), comportamento de antes.
      const inserted = cc.insertText(
        textoCitacao(rotuloOuAntigo(attachment?.rotulo, index || 0), citationText),
        Word.InsertLocation.replace,
      );

      const ccRange = cc.getRange();
      const ccUrl = getAttachmentUrl(attachment);
      try {
        if (inserted) {
          inserted.styleBuiltIn = Word.BuiltInStyleName.hyperlink;
          inserted.hyperlink = ccUrl;
        } else {
          ccRange.styleBuiltIn = Word.BuiltInStyleName.hyperlink;
          ccRange.hyperlink = ccUrl;
        }
      } catch {}

      try {
        const hits = ccRange.search("vide", {
          matchCase: false,
          matchWholeWord: true,
        });
        hits.load("items");
        await context.sync();
        for (const h of hits.items || []) {
          try {
            h.font.italic = true;
          } catch {}
        }
      } catch {}

      try {
        const afterCcRange = cc.getRange(Word.RangeLocation.after);
        afterCcRange.select(Word.SelectionMode.start);
      } catch {}

      await context.sync();
    });
  } catch (error) {
    console.error("Erro ao inserir citação:", error);
    throw error;
  }
};

export const updateAttachmentCitations = async (activeAttachments) => {
  if (!activeAttachments || activeAttachments.length === 0) return;

  try {
    await Word.run(async (context) => {
      const positionMap = new Map();
      activeAttachments.forEach((item, index) => {
        positionMap.set(String(item.id), {
          index: index + 1,
          rotulo: item.rotulo,
          desc: item.description,
          url: getAttachmentUrl(item),
        });
      });

      const ccs = context.document.contentControls;
      ccs.load("items/tag, items/text, items/cannotEdit, items/cannotDelete");
      await context.sync();

      // Coleta citacoes cujo anexo foi apagado (nao esta mais em positionMap)
      // para remover a citacao orfa do documento ao final.
      const orphaned = [];

      for (let i = 0; i < ccs.items.length; i++) {
        const cc = ccs.items[i];
        try {
          if (!cc.tag || !cc.tag.startsWith("ANEXO_ID_")) continue;

          const fileId = cc.tag.replace("ANEXO_ID_", "");
          const mapped = positionMap.get(fileId);
          if (!mapped) {
            orphaned.push(cc);
            continue;
          }

          const prevCannotEdit = Boolean(cc.cannotEdit);
          const prevCannotDelete = Boolean(cc.cannotDelete);

          try {
            cc.cannotEdit = false;
            cc.cannotDelete = false;
          } catch {}

          const citationText = cleanDescForCitation(mapped.desc);
          // Espelho: rótulo pronto do Portal; sem ele, `mapped.index` (base 1) como antes.
          const newText = textoCitacao(
            rotuloOuAntigo(mapped.rotulo, mapped.index - 1),
            citationText,
          );

          const targetRange =
            cc.text !== newText
              ? cc.insertText(newText, Word.InsertLocation.replace)
              : cc.getRange();
          const url = String(mapped.url || "").trim();
          try {
            targetRange.hyperlink = url;
          } catch {}
          try {
            targetRange.styleBuiltIn = Word.BuiltInStyleName.hyperlink;
          } catch {}

          try {
            const ccRange = cc.getRange();
            const hits = ccRange.search("vide", {
              matchCase: false,
              matchWholeWord: true,
            });
            hits.load("items");
            await context.sync();
            for (const h of hits.items || []) {
              try {
                h.font.italic = true;
              } catch {}
            }
          } catch {}

          try {
            cc.cannotEdit = prevCannotEdit;
            cc.cannotDelete = prevCannotDelete;
          } catch {}
        } catch {
          continue;
        }
      }

      // Remove citacoes de anexos que foram apagados do Drive/lista.
      // Segue o mesmo padrao de removeAttachmentCitations (cc.delete(false)),
      // mantendo o texto da citacao no documento sem o marcador de anexo.
      for (const orphanCc of orphaned) {
        try {
          orphanCc.cannotEdit = false;
          orphanCc.cannotDelete = false;
        } catch {}
        try {
          orphanCc.delete(false);
        } catch {}
      }

      await context.sync();
    });
  } catch (error) {
    console.error("Erro ao atualizar citações:", error);
    throw error;
  }
};

export const migrateLegacyAttachmentCitations = async (activeAttachments) => {
  if (!window?.Word) return;
  if (!Array.isArray(activeAttachments) || activeAttachments.length === 0)
    return;

  const byUrl = new Map();
  const byViewer = new Map();
  const byNumber = new Map();
  for (const item of activeAttachments) {
    const id = String(item?.id || "").trim();
    if (!id) continue;
    const url = String(item?.url || "").trim();
    if (url) byUrl.set(url, id);
    byViewer.set(`${BASE_VIEWER_URL}/${id}`, id);
  }

  for (let i = 0; i < activeAttachments.length; i += 1) {
    const id = String(activeAttachments[i]?.id || "").trim();
    if (!id) continue;
    byNumber.set(i + 1, id);
  }

  try {
    await window.Word.run(async (context) => {
      const ccs = context.document.contentControls;
      ccs.load("items/tag, items/title, items/text");
      await context.sync();

      const ranges = [];
      for (const cc of ccs.items || []) {
        const tag = String(cc?.tag || "");
        if (tag.startsWith("ANEXO_ID_")) continue;
        const title = String(cc?.title || "").trim();
        const text = String(cc?.text || "");
        if (!title && !text) continue;
        if (title && title !== "Citação de Anexo") continue;
        if (
          !/ANEXO\s*\d+/i.test(text) &&
          !/\(\s*ANEXO\s*\d+/i.test(text) &&
          !/\(\s*Vide\s+/i.test(text)
        )
          continue;
        const r = cc.getRange();
        r.load("hyperlink");
        ranges.push({ cc, range: r });
      }

      if (!ranges.length) return;
      await context.sync();

      for (const item of ranges) {
        const link = String(item?.range?.hyperlink || "").trim();
        let id = link ? byUrl.get(link) || byViewer.get(link) : null;

        if (!id) {
          const text = String(item?.cc?.text || "").trim();
          const m = text.match(/ANEXO\s*(\d{1,3})/i);
          const n = m?.[1] ? Number(m[1]) : NaN;
          if (Number.isFinite(n) && n > 0) {
            id = byNumber.get(n) || null;
          }
        }

        if (!id) continue;
        item.cc.tag = `ANEXO_ID_${id}`;
      }

      await context.sync();
    });
  } catch {
    return;
  }
};

export const ensureFinalAttachmentsListAtEnd = async (attachments) => {
  const W = window?.Word;
  if (!W || !Array.isArray(attachments) || attachments.length === 0) return;

  try {
    await W.run(async (context) => {
      const body = context.document.body;

      const allCCs = context.document.contentControls;
      allCCs.load("items/tag");
      const controlsA =
        context.document.contentControls.getByTag("RDA_FINAL_LIST");
      const controlsB =
        context.document.contentControls.getByTag("ANEXO_LISTA_FINAL");
      controlsA.load("items, items/cannotEdit, items/cannotDelete");
      controlsB.load("items, items/cannotEdit, items/cannotDelete");
      await context.sync();

      const citedIds = new Set();
      for (const cc of allCCs.items || []) {
        const tag = String(cc?.tag || "");
        if (tag.startsWith("ANEXO_ID_")) {
          citedIds.add(tag.replace("ANEXO_ID_", ""));
        }
      }

      const cited = attachments.filter((item) => {
        if (typeof item === "string") return false;
        return citedIds.has(String(item?.id || "").trim());
      });

      if (cited.length === 0) return;

      let listControl;
      if (controlsA.items.length > 0) {
        listControl = controlsA.items[0];
      } else if (controlsB.items.length > 0) {
        listControl = controlsB.items[0];
        listControl.tag = "RDA_FINAL_LIST";
        listControl.title = "Lista de Anexos RDA";
      } else {
        const anchor = body.insertParagraph("", W.InsertLocation.end);
        anchor.styleBuiltIn = W.BuiltInStyleName.normal;
        listControl = anchor.insertContentControl();
        listControl.tag = "RDA_FINAL_LIST";
        listControl.title = "Lista de Anexos RDA";
      }

      try {
        if (listControl && typeof listControl.load === "function") {
          listControl.load("cannotEdit,cannotDelete");
        }
      } catch {}

      try {
        await context.sync();
      } catch {}

      const prevCannotEdit = Boolean(listControl.cannotEdit);
      const prevCannotDelete = Boolean(listControl.cannotDelete);
      listControl.cannotEdit = false;
      listControl.cannotDelete = false;

      if (typeof listControl.clear === "function") {
        listControl.clear();
      } else {
        const r0 = listControl.getRange();
        if (r0?.clear) r0.clear();
      }

      for (let i = 0; i < cited.length; i += 1) {
        const item = cited[i];
        const rawText =
          typeof item === "string"
            ? item
            : String(
                item?.text ||
                  item?.description ||
                  item?.name ||
                  item?.finalName ||
                  "Evidência Documental",
              );
        const cleanDesc = cleanDescForCitation(rawText);
        // Espelho: cada linha mostra o rótulo pronto do próprio anexo (pode ser esparso: 30, 31, 35),
        // igual ao do Portal e ao das citações. Sem rótulo, `i + 1` como antes.
        const rotuloDaLinha = typeof item === "string" ? undefined : item?.rotulo;
        const lineText = `${rotuloOuAntigo(rotuloDaLinha, i)} - ${cleanDesc}`;
        const p = listControl.insertParagraph(lineText, W.InsertLocation.end);
        p.styleBuiltIn = W.BuiltInStyleName.normal;
        const r = p.getRange();
        const id =
          typeof item === "string" ? "" : String(item?.id || "").trim();
        const url =
          typeof item === "string"
            ? ""
            : getAttachmentUrl({ id, url: item?.url });
        if (url) {
          r.hyperlink = url;
          r.styleBuiltIn = W.BuiltInStyleName.hyperlink;
        }
      }

      listControl.cannotEdit = true;
      listControl.cannotDelete = true;

      if (!prevCannotEdit) listControl.cannotEdit = false;
      if (!prevCannotDelete) listControl.cannotDelete = false;

      await context.sync();
    });
  } catch (err) {
    console.error("Erro na Lista Final Office.js:", err);
    throw err;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// injectQualificationAtCursor — Padrão Ouro OOXML Injection (FIX V2.0)
// ═══════════════════════════════════════════════════════════════════════════════
// FIX 09/07/2026: Remove <w:sdt> do fragmento OOXML para evitar ContentControl
// aninhado. O Office.js não aceita insertOoxml com <w:sdt> dentro de outro SDT.
// Fallback: Range.insertText + ContentControl wrapper externo.
// ═══════════════════════════════════════════════════════════════════════════════

function _escapeXmlBasic(unsafe) {
  var s = String(unsafe || "");
  var r = "";
  for (var i = 0; i < s.length; i++) {
    var c = s.charCodeAt(i);
    if (c === 38) {
      r += String.fromCharCode(38, 97, 109, 112, 59);
      continue;
    } // &
    if (c === 60) {
      r += String.fromCharCode(38, 108, 116, 59);
      continue;
    } // <
    if (c === 62) {
      r += String.fromCharCode(38, 103, 116, 59);
      continue;
    } // >
    if (c === 39) {
      r += String.fromCharCode(38, 97, 112, 111, 115, 59);
      continue;
    } // '
    if (c === 34) {
      r += String.fromCharCode(38, 113, 117, 111, 116, 59);
      continue;
    } // "
    r += s.charAt(i);
  }
  return r;
}

function _buildPartyQualText(party) {
  // PRIORIDADE 1: Usar party.text (bridge → já formatado no Word)
  // Quando os dados vêm do bridge (Word Add-in), o objeto party só tem
  // {id, role, name, text, ooxml} — sem sub-objeto qualification.
  // O party.text já contém o texto completo formatado (ex: "[CLIENTE_D],
  // brasileiro, solteiro, ...").
  var plainText = String((party && party.text) || "").trim();
  if (plainText) {
    var name = String((party && party.name) || "")
      .trim()
      .toUpperCase();
    if (plainText.toUpperCase().startsWith(name)) {
      var afterName = plainText.slice(name.length).trim();
      if (afterName) {
        return ", " + afterName.replace(/^[,:\s]+/, "");
      }
    }
    return ", " + plainText;
  }

  // PRIORIDADE 2: Fallback para qualification estruturado (SALI LMSS / Firestore)
  var q = (party && party.qualification) || {};
  var chunks = [];
  if (q.nationality) chunks.push("nacionalidade: " + q.nationality);
  if (q.marital_status) chunks.push("estado civil: " + q.marital_status);
  if (q.profession) chunks.push("profissão: " + q.profession);
  if (q.document_rg) chunks.push("RG " + q.document_rg);
  if (q.document_cpf) chunks.push("CPF " + q.document_cpf);
  if (q.document_cnpj) chunks.push("CNPJ " + q.document_cnpj);
  if (q.address) chunks.push("residente na " + q.address);

  if (chunks.length > 0) {
    return ", " + chunks.join(", ") + ".";
  }

  return "";
}

// ═══════════════════════════════════════════════════════════════════════════════
// _buildPartyOoxmlContent — Gera fragmento OOXML <w:p> SEM <w:sdt>
// ═══════════════════════════════════════════════════════════════════════════════
// FIX 09/07/2026: Não gerar <w:sdt> aninhado. Apenas <w:p> com runs formatados.
// Isso permite insertOoxml dentro de um ContentControl existente sem conflito.
// ═══════════════════════════════════════════════════════════════════════════════
function _buildPartyOoxmlContent(party) {
  var rawName = String((party && party.name) || "")
    .trim()
    .toUpperCase();
  if (!rawName) rawName = "PARTE INDETERMINADA";

  var qualText = _buildPartyQualText(party);
  var safeName = _escapeXmlBasic(rawName);
  var safeQual = _escapeXmlBasic(qualText);

  var ns = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

  // Fragmento OOXML APENAS <w:p> com <w:r> formatado — SEM <w:sdt>
  // Compatível com insertOoxml dentro de ContentControl
  return (
    '<w:p xmlns:w="' +
    ns +
    '">' +
    '<w:pPr><w:spacing w:after="0" w:line="240" w:lineRule="auto"/></w:pPr>' +
    "<w:r>" +
    '<w:rPr><w:b/><w:color w:val="1A1A1A"/></w:rPr>' +
    "<w:t>" +
    safeName +
    "</w:t>" +
    "</w:r>" +
    "<w:r>" +
    "<w:t>" +
    safeQual +
    "</w:t>" +
    "</w:r>" +
    "</w:p>"
  );
}

export async function injectQualificationAtCursor(party) {
  if (!window || !window.Word)
    throw new Error(
      "Word API indisponível. Abra este painel dentro do Microsoft Word (taskpane).",
    );

  var id = String((party && (party.id || party.clientId)) || "").trim();
  var role = String(
    (party && (party.player_role || party.role)) || "PARTE",
  ).trim();
  var name = String((party && party.name) || "")
    .trim()
    .toUpperCase();
  if (!name) name = "PARTE INDETERMINADA";

  // ═══ FIX P20 (09/07/2026): ═══
  // Totalmente refeito usando Office.js coercion (setSelectedDataAsync),
  // que NÃO usa o pipeline Word.run / OOXML / ContentControl.
  // Compatível com Word build 18021 que tem bugs com insertOoxml e insertContentControl.
  var qualText = _buildPartyQualText(party);
  var fullText = name + qualText;

  return new Promise(function (resolve) {
    try {
      window.Office.context.document.setSelectedDataAsync(
        fullText,
        { coercionType: window.Office.CoercionType.Text },
        function (asyncResult) {
          resolve(
            asyncResult &&
              asyncResult.status === window.Office.AsyncResultStatus.Succeeded,
          );
        },
      );
    } catch (e) {
      try {
        console.warn("[inject] setSelectedDataAsync falhou.", e);
      } catch (_) {}
      resolve(false);
    }
  });
}

export const injectPartyData = async (party) => {
  return injectQualificationAtCursor(party);
};

export const replaceSelectedTextWithAI = async (
  systemPrompt,
  uiCallbackStates,
) => {
  if (!window?.Word)
    throw new Error(
      "Word API indisponível. Abra este painel dentro do Microsoft Word (taskpane).",
    );

  uiCallbackStates = uiCallbackStates || {};
  var prompt = String(systemPrompt || "").trim();
  if (!prompt) throw new Error("Prompt do Copilot Jurídico ausente.");

  var setLoading =
    typeof uiCallbackStates.setLoading === "function"
      ? uiCallbackStates.setLoading
      : null;
  var setPreview =
    typeof uiCallbackStates.setPreview === "function"
      ? uiCallbackStates.setPreview
      : null;

  try {
    if (setLoading) setLoading(true);

    return await Word.run(async function (context) {
      var range = context.document.getSelection();
      range.load("text");
      await context.sync();

      var selectedText = String((range && range.text) || "").trim();
      if (!selectedText)
        throw new Error("Selecione um texto no Word primeiro.");

      var aiResponse = await generateTextWithGemini(prompt, selectedText, {
        tier: "pro",
      });
      var revisedText = String(aiResponse || "").trim();
      if (!revisedText)
        throw new Error("A IA retornou um texto vazio para revisão.");

      range.insertText(revisedText, Word.InsertLocation.replace);
      await context.sync();

      if (setPreview) setPreview(revisedText);
      return revisedText;
    });
  } finally {
    if (setLoading) setLoading(false);
  }
};

var SETTINGS_KEY_ATTACHMENTS = "rda_attachments_v1";

var ensureArray = function (value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [];
};

var toErrorMessage = function (e) {
  return String((e && e.message) || e || "Erro desconhecido");
};

export var loadAttachmentsFromDocument = async function () {
  if (
    !window ||
    !window.Office ||
    !window.Office.context ||
    !window.Office.context.document ||
    !window.Office.context.document.settings
  )
    return [];

  var settings = window.Office.context.document.settings;

  await new Promise(function (resolve, reject) {
    try {
      settings.refreshAsync(function (ar) {
        if (ar && ar.status === window.Office.AsyncResultStatus.Succeeded)
          resolve();
        else
          reject(
            new Error(
              (ar && ar.error && ar.error.message) ||
                "Falha ao sincronizar settings do documento.",
            ),
          );
      });
    } catch (e) {
      reject(e);
    }
  });

  var raw = settings.get(SETTINGS_KEY_ATTACHMENTS);
  return ensureArray(raw);
};

export var saveAttachmentsToDocument = async function (attachmentsArray) {
  if (
    !window ||
    !window.Office ||
    !window.Office.context ||
    !window.Office.context.document ||
    !window.Office.context.document.settings
  ) {
    throw new Error(
      "Office.js indisponível neste ambiente. Abra dentro do Word.",
    );
  }

  var settings = window.Office.context.document.settings;
  settings.set(SETTINGS_KEY_ATTACHMENTS, ensureArray(attachmentsArray));

  await new Promise(function (resolve, reject) {
    try {
      settings.saveAsync(function (ar) {
        if (ar && ar.status === window.Office.AsyncResultStatus.Succeeded)
          resolve();
        else
          reject(
            new Error(
              (ar && ar.error && ar.error.message) ||
                "Falha ao salvar settings no documento.",
            ),
          );
      });
    } catch (e) {
      reject(e);
    }
  });
};

export var removeAttachmentCitations = async function (attachmentId) {
  var id = String(attachmentId || "").trim();
  if (!id) return;
  var tagToRemove = "ANEXO_ID_" + id;

  try {
    await Word.run(async function (context) {
      var controls = context.document.contentControls;
      controls.load("items/tag");
      await context.sync();

      for (var ci = 0; ci < (controls.items || []).length; ci++) {
        var cc = controls.items[ci];
        if (String((cc && cc.tag) || "") === tagToRemove) cc.delete(false);
      }
      await context.sync();
    });
  } catch (e) {
    console.error("Erro ao remover citações:", e);
  }
};

export var insertFinalAttachmentsList = async function (attachmentsArray) {
  var atts = ensureArray(attachmentsArray);
  try {
    await Word.run(async function (context) {
      var range = context.document.getSelection();
      var anchor = range.insertParagraph("", Word.InsertLocation.after);
      var cc = anchor.insertContentControl();
      cc.tag = "ANEXO_LISTA_FINAL";
      cc.title = "Lista de Anexos Consolidados";
      cc.appearance = Word.ContentControlAppearance.boundingBox;
      await context.sync();
    });
  } catch (e) {
    console.error("Erro ao inserir lista final no cursor:", e);
    throw e;
  }

  await ensureFinalAttachmentsListAtEnd(atts);
};

export var addAttachmentAndSave = async function (
  currentArray,
  nextAttachment,
) {
  var next = [].concat(ensureArray(currentArray), [nextAttachment]);
  await saveAttachmentsToDocument(next);
  try {
    await updateAttachmentCitations(next);
  } catch (_) {}
  return next;
};

export var removeAttachmentAtAndSave = async function (currentArray, index) {
  var cur = ensureArray(currentArray);
  var next = cur.filter(function (_, i) {
    return i !== index;
  });
  await saveAttachmentsToDocument(next);
  try {
    await updateAttachmentCitations(next);
  } catch (_) {}
  return next;
};

export var tryLoadAttachmentsSafe = async function () {
  try {
    return await loadAttachmentsFromDocument();
  } catch (e) {
    return { error: toErrorMessage(e), attachments: [] };
  }
};
